import React from 'react';
import TodoItem from './TodoItem';
import TodoNode from './TodoNode';
import SwimlaneGroup from './SwimlaneGroup';
import TodoListEmpty from './TodoListEmpty';
import AddMissionRow from './AddMissionRow';

/**
 * Renders the appropriate content based on view type (tree, list, or swimlane)
 */
const TodoListContent = ({
  viewData,
  tasks,
  statuses,
  allTags,
  lockStates,
  showArchived,
  // Action handlers
  handleToggleComplete,
  handleDrillIn,
  handleAddMissionClick,
  // Task callbacks
  onOpenDetails,
  onOpenAttachments,
  onStatusChange,
  onPriorityChange,
  onDifficultyChange,
  onDueDateChange,
  onRenameTask,
  onDeleteTask,
  onArchiveTask,
  onUnarchiveTask,
  onDependencyChange,
  onConditionsChange,
  onFocusTask,
  onAddSubtask,
  onIconChange,
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  onSetStatusAutoSync,
  // Selection for Fokus mode
  selectedTaskIds = new Set(),
  onToggleSelect,
  // Element visibility
  elementVisibility = {},
  // View navigation (for "Open in view" dropdown)
  currentView = 'todolist',
  customViews = [],
  activeCustomViewId = null,
  // Current campaign ID (for detecting cross-campaign navigation)
  currentCampaignId = null,
  onViewChange,
  onSelectCustomView,
  onGoToTaskInView,
  // Decision handlers
  onDecisionAnswerChange,
  onDecisionOptionsChange,
  // Empty state arrow indicator
  onShowAddMissionArrow,
  // For adding missions to nested sprints in Master view
  onAddMissionToSprint,
  // Custom fields
  customFields = [],
  onCustomFieldValueChange,
  // Deliverables warning modal
  onSubmitDeliverable,
  onNavigateToParentMission,
  nestedSprints = [],
  allSprints = [],
}) => {
  // Empty state - check if filtered (tasks exist but viewData is empty)
  if (viewData.data.length === 0 && viewData.type !== 'swimlane') {
    const isFiltered = tasks.length > 0; // If tasks exist but viewData is empty, filters are hiding them
    return (
      <TodoListEmpty
        showArchived={showArchived}
        isFiltered={isFiltered}
        onShowAddMissionArrow={onShowAddMissionArrow}
      />
    );
  }

  // Tree view (Master mode)
  if (viewData.type === 'tree') {
    return (
      <div className="todolist-view__list">
        {viewData.data.map((node, index) => (
          <TodoNode
            key={`${node.task.id}-${node.depth}-${index}`}
            node={node}
            statuses={statuses}
            allTags={allTags}
            allTasks={tasks}
            lockStates={lockStates}
            onToggleComplete={handleToggleComplete}
            onOpenDetails={onOpenDetails}
            onOpenAttachments={onOpenAttachments}
            onStatusChange={onStatusChange}
            onPriorityChange={onPriorityChange}
            onDifficultyChange={onDifficultyChange}
            onDueDateChange={onDueDateChange}
            onRenameTask={onRenameTask}
            onDeleteTask={onDeleteTask}
            onArchiveTask={onArchiveTask}
            onUnarchiveTask={onUnarchiveTask}
            onDependencyChange={onDependencyChange}
            onConditionsChange={onConditionsChange}
            onFocusTask={onFocusTask}
            onAddSubtask={onAddSubtask}
            onDrillIn={handleDrillIn}
            onIconChange={onIconChange}
            onAddStatus={onAddStatus}
            onDeleteStatus={onDeleteStatus}
            onRenameStatus={onRenameStatus}
            onSetStatusAutoSync={onSetStatusAutoSync}
            selectedTaskIds={selectedTaskIds}
            onToggleSelect={onToggleSelect}
            elementVisibility={elementVisibility}
            currentView={currentView}
            customViews={customViews}
            activeCustomViewId={activeCustomViewId}
            currentCampaignId={currentCampaignId}
            onViewChange={onViewChange}
            onSelectCustomView={onSelectCustomView}
            onGoToTaskInView={onGoToTaskInView}
            onDecisionAnswerChange={onDecisionAnswerChange}
            onDecisionOptionsChange={onDecisionOptionsChange}
            onAddMissionToSprint={onAddMissionToSprint}
            customFields={customFields}
            onCustomFieldValueChange={onCustomFieldValueChange}
            onNavigateToParentMission={onNavigateToParentMission}
            nestedSprints={nestedSprints}
            allSprints={allSprints}
          />
        ))}
        {/* Add Mission button at root level */}
        <AddMissionRow depth={0} onAddMission={handleAddMissionClick} />
      </div>
    );
  }

  // List view (Simple mode)
  if (viewData.type === 'list') {
    return (
      <div className="todolist-view__list todolist-view__list--simple">
        {viewData.data.map((item, index) => {
          const taskStatus = statuses.find(s => s.id === item.task.status);
          const isChecked = item.isSubtask 
            ? item.task.completed === true
            : taskStatus?.category === 'completed';
          const lockState = lockStates[item.task.id];
          const blockingDeps = lockState?.blockingTasks || [];
          const isLocked = lockState?.isLocked && item.task.status !== 'completed';
          
          return (
            <TodoItem
              key={`${item.task.id}-${index}`}
              task={item.task}
              depth={item.depth}
              isSubtask={item.isSubtask}
              isParent={false}
              nestedSprint={item.nestedSprint}
              hasNestedCampaign={item.hasNestedCampaign}
              blockingDependencies={blockingDeps}
              parentMissionName={lockState?.parentMissionName}
              parentCampaignName={lockState?.parentCampaignName}
              parentMissionId={lockState?.parentMissionId}
              parentSprintId={lockState?.parentSprintId}
              isLocked={isLocked}
              lockReason={lockState?.lockReason}
              statuses={statuses}
              allTags={allTags}
              allTasks={item.sprintTasks || tasks}
              isChecked={isChecked}
              onToggleComplete={() => handleToggleComplete(item.task.id, item.isSubtask, item.parentTask?.id)}
              onOpenDetails={() => !item.isSubtask && onOpenDetails?.(item.task.id)}
              onOpenAttachments={!item.isSubtask ? onOpenAttachments : undefined}
              onStatusChange={!item.isSubtask ? onStatusChange : undefined}
              onPriorityChange={!item.isSubtask ? onPriorityChange : undefined}
              onDifficultyChange={!item.isSubtask ? onDifficultyChange : undefined}
              onDueDateChange={!item.isSubtask ? onDueDateChange : undefined}
              onRename={!item.isSubtask ? onRenameTask : undefined}
              onDelete={!item.isSubtask ? onDeleteTask : undefined}
              onArchive={!item.isSubtask ? onArchiveTask : undefined}
              onUnarchive={!item.isSubtask ? onUnarchiveTask : undefined}
              onDependencyChange={!item.isSubtask ? onDependencyChange : undefined}
              onConditionsChange={!item.isSubtask ? onConditionsChange : undefined}
              onFocusTask={!item.isSubtask ? onFocusTask : undefined}
              onAddSubtask={!item.isSubtask ? onAddSubtask : undefined}
              onDrillIn={item.nestedSprint ? () => handleDrillIn(item.nestedSprint) : undefined}
              onIconChange={!item.isSubtask ? onIconChange : undefined}
              onAddStatus={!item.isSubtask ? onAddStatus : undefined}
              onDeleteStatus={!item.isSubtask ? onDeleteStatus : undefined}
              onRenameStatus={!item.isSubtask ? onRenameStatus : undefined}
              onSetStatusAutoSync={!item.isSubtask ? onSetStatusAutoSync : undefined}
              isSelected={!item.isSubtask && selectedTaskIds.has(item.task.id)}
              onToggleSelect={!item.isSubtask ? onToggleSelect : undefined}
              elementVisibility={elementVisibility}
              currentView={currentView}
              customViews={customViews}
              activeCustomViewId={activeCustomViewId}
              sourceCampaignId={item.sprintId || null}
              onViewChange={onViewChange}
              onSelectCustomView={onSelectCustomView}
              onGoToTaskInView={onGoToTaskInView}
              onDecisionAnswerChange={!item.isSubtask ? onDecisionAnswerChange : undefined}
              onDecisionOptionsChange={!item.isSubtask ? onDecisionOptionsChange : undefined}
              customFields={!item.isSubtask ? customFields : []}
              onCustomFieldValueChange={!item.isSubtask ? onCustomFieldValueChange : undefined}
              onSubmitDeliverable={!item.isSubtask ? onSubmitDeliverable : undefined}
              onNavigateToParentMission={onNavigateToParentMission}
              nestedSprints={nestedSprints}
              allSprints={allSprints}
            />
          );
        })}
        {/* Add Mission button */}
        <AddMissionRow depth={0} onAddMission={handleAddMissionClick} />
      </div>
    );
  }

  // Swimlane view
  if (viewData.type === 'swimlane') {
    return (
      <div className="todolist-view__swimlanes">
        {viewData.data.map(group => (
          <SwimlaneGroup
            key={group.id}
            group={group}
            statuses={statuses}
            allTags={allTags}
            allTasks={tasks}
            lockStates={lockStates}
            onToggleComplete={handleToggleComplete}
            onOpenDetails={onOpenDetails}
            onOpenAttachments={onOpenAttachments}
            onStatusChange={onStatusChange}
            onPriorityChange={onPriorityChange}
            onDifficultyChange={onDifficultyChange}
            onDueDateChange={onDueDateChange}
            onRenameTask={onRenameTask}
            onDeleteTask={onDeleteTask}
            onArchiveTask={onArchiveTask}
            onUnarchiveTask={onUnarchiveTask}
            onDependencyChange={onDependencyChange}
            onConditionsChange={onConditionsChange}
            onFocusTask={onFocusTask}
            onAddSubtask={onAddSubtask}
            onDrillIn={handleDrillIn}
            onIconChange={onIconChange}
            onAddStatus={onAddStatus}
            onDeleteStatus={onDeleteStatus}
            onRenameStatus={onRenameStatus}
            onSetStatusAutoSync={onSetStatusAutoSync}
            selectedTaskIds={selectedTaskIds}
            onToggleSelect={onToggleSelect}
            elementVisibility={elementVisibility}
            currentView={currentView}
            customViews={customViews}
            activeCustomViewId={activeCustomViewId}
            currentCampaignId={currentCampaignId}
            onViewChange={onViewChange}
            onSelectCustomView={onSelectCustomView}
            onGoToTaskInView={onGoToTaskInView}
            onDecisionAnswerChange={onDecisionAnswerChange}
            onDecisionOptionsChange={onDecisionOptionsChange}
            customFields={customFields}
            onCustomFieldValueChange={onCustomFieldValueChange}
            onSubmitDeliverable={onSubmitDeliverable}
            onNavigateToParentMission={onNavigateToParentMission}
            nestedSprints={nestedSprints}
            allSprints={allSprints}
          />
        ))}
      </div>
    );
  }

  return null;
};

export default TodoListContent;
